import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subscription, firstValueFrom } from "rxjs";
import { GlobalModalService } from "../../services/global-modal.service";
import { MfaService } from "../../services/mfa.service";
import { AuthService } from "@auth0/auth0-angular";
// Consts
import { environment as ENV } from "@environments/environment";

type Language = 'es' | 'en' | 'pt';

interface Translations {
  [lang: string]: {
    [key: string]: string;
  };
}

@Component({
  selector: "app-global-modal",
  templateUrl: "./global-modal.component.html",
  styleUrls: ["./global-modal.component.scss"],
})
export class GlobalModalComponent implements OnInit, OnDestroy {
  isVisible = false;
  private subscription: Subscription;

  // Language properties
  currentLanguage: Language = 'es';
  languages: { code: Language; label: string }[] = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' }
  ];

  // Translations
  private translations: Translations = {
    es: {
      title: 'Actualización de seguridad',
      description: 'Para mantener tu cuenta segura, completa el proceso de doble autenticación.',
      step1: 'Instala una app de autenticación en tu celular. Nuestra recomendación es',
      step1Secondary: 'Abre la app y dale clic a agregar cuenta.',
      downloadAppStore: 'Descárgala en tu app store',
      step2: 'Escanea este código QR con tu cámara',
      loadingQR: 'Cargando código QR...',
      qrError: 'No se pudo generar el código QR',
      getAccess: 'Obtener acceso',
      loading: 'Cargando...',
      cannotScanQR: '¿No puedes escanear el código QR?',
      copyCode: 'Copia este código en la app:',
      copyButton: 'Copiar código',
      step3: 'Ingresa el código de verificación brindado por la app.',
      codePlaceholder: 'Código',
      remindLater: 'Recordarme más tarde',
      ready: 'Listo',
      verifying: 'Verificando...',
      errorQRGenerate: 'No se pudo generar el código QR. Por favor, intenta nuevamente.',
      errorInitialize: 'Error al inicializar la autenticación de dos factores. Por favor, intenta nuevamente.',
      errorAssociate: 'Error al obtener el token o asociar el TOTP. Por favor, intenta nuevamente.',
      errorCodeLength: 'Por favor, ingresa un código de 6 dígitos.',
      errorNoToken: 'No se ha generado el token de MFA. Por favor, intenta nuevamente.',
      successMFA: '¡Autenticación de dos factores configurada exitosamente!',
      errorInvalidCode: 'Código inválido. Por favor, verifica e intenta nuevamente.',
      errorVerify: 'Error al verificar el código. Por favor, intenta nuevamente.',
      successCopied: 'Código copiado al portapapeles',
      errorCopy: 'No se pudo copiar el código. Por favor, cópialo manualmente.',
      errorEnterCode: 'Por favor, ingresa el código de verificación antes de continuar.'
    },
    en: {
      title: 'Security update',
      description: 'To keep your account secure, complete the two-factor authentication process.',
      step1: 'Install an authentication app on your phone. We recommend',
      step1Secondary: 'Open the app and click add account.',
      downloadAppStore: 'Download it from your app store',
      step2: 'Scan this QR code with your camera',
      loadingQR: 'Loading QR code...',
      qrError: 'Could not generate QR code',
      getAccess: 'Get access',
      loading: 'Loading...',
      cannotScanQR: 'Can\'t scan the QR code?',
      copyCode: 'Copy this code in the app:',
      copyButton: 'Copy code',
      step3: 'Enter the verification code provided by the app.',
      codePlaceholder: 'Code',
      remindLater: 'Remind me later',
      ready: 'Ready',
      verifying: 'Verifying...',
      errorQRGenerate: 'Could not generate QR code. Please try again.',
      errorInitialize: 'Error initializing two-factor authentication. Please try again.',
      errorAssociate: 'Error getting token or associating TOTP. Please try again.',
      errorCodeLength: 'Please enter a 6-digit code.',
      errorNoToken: 'MFA token has not been generated. Please try again.',
      successMFA: 'Two-factor authentication configured successfully!',
      errorInvalidCode: 'Invalid code. Please verify and try again.',
      errorVerify: 'Error verifying code. Please try again.',
      successCopied: 'Code copied to clipboard',
      errorCopy: 'Could not copy code. Please copy it manually.',
      errorEnterCode: 'Please enter the verification code before continuing.'
    },
    pt: {
      title: 'Atualização de segurança',
      description: 'Para manter sua conta segura, complete o processo de autenticação de dois fatores.',
      step1: 'Instale um aplicativo de autenticação no seu celular. Nossa recomendação é',
      step1Secondary: 'Abra o aplicativo e clique em adicionar conta.',
      downloadAppStore: 'Baixe na sua loja de aplicativos',
      step2: 'Escaneie este código QR com sua câmera',
      loadingQR: 'Carregando código QR...',
      qrError: 'Não foi possível gerar o código QR',
      getAccess: 'Obter acesso',
      loading: 'Carregando...',
      cannotScanQR: 'Não consegue escanear o código QR?',
      copyCode: 'Copie este código no aplicativo:',
      copyButton: 'Copiar código',
      step3: 'Digite o código de verificação fornecido pelo aplicativo.',
      codePlaceholder: 'Código',
      remindLater: 'Lembrar mais tarde',
      ready: 'Pronto',
      verifying: 'Verificando...',
      errorQRGenerate: 'Não foi possível gerar o código QR. Por favor, tente novamente.',
      errorInitialize: 'Erro ao inicializar a autenticação de dois fatores. Por favor, tente novamente.',
      errorAssociate: 'Erro ao obter o token ou associar o TOTP. Por favor, tente novamente.',
      errorCodeLength: 'Por favor, digite um código de 6 dígitos.',
      errorNoToken: 'O token MFA não foi gerado. Por favor, tente novamente.',
      successMFA: 'Autenticação de dois fatores configurada com sucesso!',
      errorInvalidCode: 'Código inválido. Por favor, verifique e tente novamente.',
      errorVerify: 'Erro ao verificar o código. Por favor, tente novamente.',
      successCopied: 'Código copiado para a área de transferência',
      errorCopy: 'Não foi possível copiar o código. Por favor, copie manualmente.',
      errorEnterCode: 'Por favor, digite o código de verificação antes de continuar.'
    }
  };

  // Error message mapping - mapea fragmentos de texto en español a claves de traducción
  private errorMessageMap: { [key: string]: string } = {
    'No se pudo generar el código QR': 'errorQRGenerate',
    'Error al inicializar': 'errorInitialize',
    'Error al obtener el token': 'errorAssociate',
    'código de 6 dígitos': 'errorCodeLength',
    'No se ha generado el token': 'errorNoToken',
    'Código inválido': 'errorInvalidCode',
    'Error al verificar': 'errorVerify',
    'No se pudo copiar': 'errorCopy',
    'ingresa el código de verificación': 'errorEnterCode'
  };

  // Success message mapping
  private successMessageMap: { [key: string]: string } = {
    'configurada exitosamente': 'successMFA',
    'copiado al portapapeles': 'successCopied'
  };

  // MFA properties
  qrCodeUrl: string = "";
  secretCode: string = "";
  verificationCode: string = "";
  mfaToken: string = "";
  isLoading: boolean = false;
  isVerifying: boolean = false;
  isEnrolling: boolean = false;
  errorMessage: string = "";
  successMessage: string = "";
  showRetryButton: boolean = false;
  isMfaConfigured: boolean = false;

  constructor(
    private globalModalService: GlobalModalService,
    private mfaService: MfaService,
    private authService: AuthService
  ) {
    // Cargar idioma guardado o usar español por defecto
    const savedLang = localStorage.getItem('mfa_modal_language') as Language;
    if (savedLang && ['es', 'en', 'pt'].includes(savedLang)) {
      this.currentLanguage = savedLang;
    }
  }

  translate(key: string): string {
    const langTranslations = this.translations[this.currentLanguage] || this.translations['es'];
    return langTranslations[key] || key;
  }

  changeLanguage(lang: Language): void {
    this.currentLanguage = lang;
    localStorage.setItem('mfa_modal_language', lang);
    // Actualizar mensajes de error/éxito si existen
    if (this.errorMessage) {
      this.updateErrorMessages();
    }
    if (this.successMessage) {
      this.updateSuccessMessages();
    }
  }

  private updateErrorMessages(): void {
    for (const [fragment, translationKey] of Object.entries(this.errorMessageMap)) {
      if (this.errorMessage.includes(fragment)) {
        this.errorMessage = this.translate(translationKey);
        return;
      }
    }
  }

  private updateSuccessMessages(): void {
    for (const [fragment, translationKey] of Object.entries(this.successMessageMap)) {
      if (this.successMessage.includes(fragment)) {
        this.successMessage = this.translate(translationKey);
        return;
      }
    }
  }

  ngOnInit(): void {
    this.subscription = this.globalModalService
      .getModalVisibility()
      .subscribe((visible) => {
        this.isVisible = visible;
        if (this.isVisible) {
          this.initializeMfa();
        } else {
          this.resetForm();
        }
      });


    this.checkAutoOpen();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Inicializa el proceso de MFA
   */
  async initializeMfa(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = "";
    this.successMessage = "";
    this.showRetryButton = false;

    const mfaAudience = `https://${ENV.auth0.domain}/mfa/`;

    try {
      // Obtener token silenciosamente sin popups
      const mfaToken = await firstValueFrom(
        this.authService.getAccessTokenSilently({
          authorizationParams: {
            audience: mfaAudience,
            scope: "enroll read:authenticators remove:authenticators",
          },
        })
      );

      this.mfaToken = mfaToken;
      const result = await this.mfaService.associateTotp(mfaToken);

      if (result && result.secret && result.barcode_uri) {
        this.secretCode = result.secret;
        this.qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
          result.barcode_uri
        )}`;
        this.showRetryButton = false;
      } else {
        this.showRetryButton = true;
        this.errorMessage = this.translate('errorQRGenerate');
      }
    } catch (error) {
      this.errorMessage = this.translate('errorInitialize');
      this.showRetryButton = true;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Maneja el clic para inscribir MFA sin popups
   */
  async onClickEnrollMFA(): Promise<void> {
    const mfaAudience = `https://${ENV.auth0.domain}/mfa/`;

    this.isLoading = true;
    this.errorMessage = "";
    this.successMessage = "";

    try {
      const token = await firstValueFrom(
        this.authService.getAccessTokenSilently({
          authorizationParams: {
            audience: mfaAudience,
            scope: "enroll read:authenticators remove:authenticators",
          },
        })
      );

      this.mfaToken = token;
      const result = await this.mfaService.associateTotp(token);

      if (result && result.secret && result.barcode_uri) {
        this.secretCode = result.secret;
        this.qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
          result.barcode_uri
        )}`;
        this.showRetryButton = false;
      } else {
        this.showRetryButton = true;
        this.errorMessage = this.translate('errorQRGenerate');
      }
    } catch (error) {
      console.error({ errorAssociatingTotp: error });
      this.errorMessage = this.translate('errorAssociate');
      this.showRetryButton = true;
    } finally {
      this.isLoading = false;
    }
  }

  onCodeInput(event: any): void {
    if (this.isVerifying || this.isMfaConfigured) {
      return;
    }

    const value = event.target.value.replace(/\D/g, "");
    this.verificationCode = value;
    this.errorMessage = "";
    this.successMessage = "";

    if (value.length === 6) {
      this.verifyCode();
    }
  }

  async verifyCode(): Promise<void> {
    if (!this.verificationCode || this.verificationCode.length !== 6) {
      this.errorMessage = this.translate('errorCodeLength');
      return;
    }

    if (!this.mfaToken) {
      this.errorMessage = this.translate('errorNoToken');
      return;
    }

    this.isVerifying = true;
    this.errorMessage = "";
    this.successMessage = "";

    try {
      const result = await this.mfaService.verifyEnrollment(
        this.mfaToken,
        this.verificationCode
      );

      if (result) {
        this.successMessage = this.translate('successMFA');
        this.isMfaConfigured = true;
        // Notificar que MFA se activó exitosamente
        this.globalModalService.notifyMfaActivated();
      }
    } catch (error: any) {
      console.error("Error verifying code:", error);

      if (error?.error?.error === "invalid_grant") {
        this.errorMessage = this.translate('errorInvalidCode');
      } else {
        this.errorMessage = this.translate('errorVerify');
      }
      
      // Limpiar el código para permitir nuevo intento
      this.verificationCode = "";
    } finally {
      this.isVerifying = false;
    }
  }

  copySecretCode(input: HTMLInputElement): void {
    input.select();
    input.setSelectionRange(0, 99999);

    try {
      document.execCommand("copy");
      this.successMessage = this.translate('successCopied');
      setTimeout(() => {
        this.successMessage = "";
      }, 2000);
    } catch (err) {
      this.errorMessage = this.translate('errorCopy');
    }
  }

  handleReady(): void {
    if (this.isMfaConfigured) {
      this.closeModal();
    }
  }

  resetForm(): void {
    this.qrCodeUrl = "";
    this.secretCode = "";
    this.verificationCode = "";
    this.mfaToken = "";
    this.errorMessage = "";
    this.successMessage = "";
    this.isLoading = false;
    this.isVerifying = false;
    this.isEnrolling = false;
    this.showRetryButton = false;
    this.isMfaConfigured = false;
  }

  closeModal(): void {
    this.globalModalService.closeModal();
  }

  handleRemindLater(): void {
    // Guardar en localStorage que el usuario quiere que se le recuerde más tarde
    this.globalModalService.saveRemindLater();
    // Cerrar el modal
    this.closeModal();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Verifica si el usuario tiene dispositivos MFA configurados
   */
  async hasMfaDevices(): Promise<boolean> {
    try {
      const mfaAudience = `https://${ENV.auth0.domain}/mfa/`;

      const token = await firstValueFrom(
        this.authService.getAccessTokenSilently({
          authorizationParams: {
            audience: mfaAudience,
            scope: "read:authenticators",
          },
        })
      );

      const authenticators = await this.mfaService.listAuthenticators(token);
      const haveActiveOtp = authenticators.some(
        (authenticator) =>
          authenticator.authenticator_type === "otp" &&
          authenticator.active === true
      );

      return haveActiveOtp;
    } catch (error: any) {
  
      const errorMessage = error?.message || error?.error_description || error?.error || "";
      const errorString = String(errorMessage).toLowerCase();
      
      if (errorString.includes("multifactor authentication required") || 
          errorString.includes("mfa_required") ||
          error?.error === "mfa_required") {
     
        return true;
      }
      console.error("Error checking MFA devices:", error);
   
      return true;
    }
  }

  /**
   * Verifica si debe abrirse automáticamente el modal
   */
  async checkAutoOpen(): Promise<void> {
    try {
      const hasMfa = await this.hasMfaDevices();

      if (hasMfa) {
        return;
      }

      if (this.globalModalService.shouldShowAfterRemindLater()) {
        this.globalModalService.autoOpenModal();
      }
    } catch (error) {
      console.error("Error in checkAutoOpen:", error);
    }
  }
}
